import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useToast } from '../lib/store';
import type {
  AttackOutcome,
  LadderResponse,
  WarHistoryResponse,
  WarResponse,
} from '../lib/types';
import { Bar, Button, EmptyState, ErrorState, Panel, Tabs } from '../components/ui';
import { BattleReplay } from '../components/war/BattleReplay';
import { TeamBuilder } from '../components/war/TeamBuilder';
import { formatDuration, formatNumber } from '../lib/format';

/**
 * Guild Wars.
 *
 * The page is deliberately built around *choosing a target*, because that is
 * the only decision a player makes during a war — teams lock at the start and
 * battles resolve on the server. Everything else here exists to make that one
 * choice informed: how many attacks are left, who has already been hit and how
 * much that has devalued them, and what little is known about each defender.
 */
export function WarsPage() {
  const [tab, setTab] = useState('war');

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Guild Wars</h1>
          <div className="page-head__sub">
            Your Pokémon, fighting for your Guild. Teams lock when a war begins.
          </div>
        </div>
      </div>

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'war', label: 'Current War' },
          { value: 'ladder', label: 'Ladder' },
          { value: 'history', label: 'History' },
        ]}
      />

      {tab === 'war' && <CurrentWar />}
      {tab === 'ladder' && <Ladder />}
      {tab === 'history' && <History />}
    </div>
  );
}

function CurrentWar() {
  const { push } = useToast();
  const state = useAsync<WarResponse>(() => api.get('/wars'));
  const [busy, setBusy] = useState<string | null>(null);
  const [replay, setReplay] = useState<string | null>(null);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <p className="tiny muted">Checking the front…</p>;

  const { enabled, guild, rating, war, queue, canManage, lastWar } = state.data;

  if (!enabled) {
    return (
      <Panel title="⚔ Guild Wars">
        <EmptyState title="Wars are not running">
          Guild Wars are switched off while the season is being prepared.
        </EmptyState>
      </Panel>
    );
  }

  if (!guild) {
    return (
      <Panel title="⚔ Guild Wars">
        <EmptyState title="You are not in a Guild">
          Guild Wars are fought between Guilds. <Link to="/guilds">Find one to join.</Link>
        </EmptyState>
      </Panel>
    );
  }

  async function toggleQueue(enter: boolean) {
    setBusy('queue');
    try {
      const result = await api.post<{ matchedNow?: boolean }>(
        enter ? '/wars/enter' : '/wars/leave-queue',
        {},
      );
      push(
        'success',
        enter
          ? result.matchedNow
            ? 'Matched! Build your team before the war starts.'
            : 'In the queue — waiting for an opponent.'
          : 'Left the queue.',
        '⚔',
      );
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not do that.');
    } finally {
      setBusy(null);
    }
  }

  async function doAttack(defenderId: string) {
    setBusy(defenderId);
    try {
      const outcome = await api.post<AttackOutcome>('/wars/attack', { defenderId });
      push(
        outcome.winner === 'a' ? 'success' : 'info',
        outcome.winner === 'a'
          ? `Victory — ${formatNumber(outcome.points)} points`
          : 'They held. Better luck next time.',
        '⚔',
      );
      setReplay(outcome.battleId);
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not attack.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="stack">
      {rating && (
        <Panel title="◈ Standing" hint={guild.name}>
          <div className="row row--between">
            <span className="chip" style={{ background: rating.tier.accent, color: '#1a1420' }}>
              {rating.tier.name}
            </span>
            <span className="mono">{formatNumber(rating.rating)}</span>
          </div>
          <div className="tiny muted" style={{ marginTop: 4 }}>
            {rating.wins}W · {rating.losses}L — tiers rank you against Guilds of comparable level,
            so a smaller roster is not stuck at the bottom.
          </div>
        </Panel>
      )}

      {/* The result of the war that just ended — design PART 36. Shown above
          the queue so somebody returning sees how it went before being asked
          to enter another one. */}
      {!war && lastWar && (
        <Panel
          title={
            lastWar.outcome === 'win'
              ? '🏆 Victory'
              : lastWar.outcome === 'loss'
                ? '◈ Defeat'
                : '◈ Draw'
          }
          hint={`vs ${lastWar.opponent}`}
        >
          <div className="row row--between">
            <span className="mono" style={{ fontSize: 20 }}>
              {formatNumber(lastWar.scoreFor)} — {formatNumber(lastWar.scoreAgainst)}
            </span>
            {lastWar.ratingDelta != null && (
              <span className="chip">
                {lastWar.ratingDelta >= 0 ? '+' : ''}
                {lastWar.ratingDelta} rating
              </span>
            )}
          </div>

          {lastWar.awards && (
            <div className="stack-sm" style={{ marginTop: 'var(--gap-sm)' }}>
              <div className="row row--between tiny">
                <span className="muted">War MVP</span>
                <span>
                  <strong>{lastWar.awards.mvp.username}</strong>{' '}
                  <span className="muted">
                    {formatNumber(lastWar.awards.mvp.points)} pts · {lastWar.awards.mvp.wins}W
                  </span>
                </span>
              </div>
              {lastWar.awards.topDefender && (
                <div className="row row--between tiny">
                  <span className="muted">Top defender</span>
                  <span>
                    <strong>{lastWar.awards.topDefender.username}</strong>{' '}
                    <span className="muted">{lastWar.awards.topDefender.defences} held</span>
                  </span>
                </div>
              )}
              {lastWar.awards.biggestUpset && (
                <div className="row row--between tiny">
                  <span className="muted">Biggest upset</span>
                  <span>
                    <strong>{lastWar.awards.biggestUpset.username}</strong>{' '}
                    <span className="muted">{lastWar.awards.biggestUpset.points} pts</span>
                  </span>
                </div>
              )}
              {lastWar.awards.bestPokemon && (
                <div className="row row--between tiny">
                  <span className="muted">Best Pokémon</span>
                  <span>
                    <strong>{lastWar.awards.bestPokemon.name}</strong>{' '}
                    <span className="muted">
                      {lastWar.awards.bestPokemon.kos} KOs · {lastWar.awards.bestPokemon.owner}
                    </span>
                  </span>
                </div>
              )}
            </div>
          )}
        </Panel>
      )}

      {!war ? (
        <Panel title="⚔ Current War">
          {queue ? (
            <>
              <EmptyState title="Waiting for an opponent">
                Your Guild is in the queue. You will be matched against one of similar standing and
                Pokémon level — which is why a smaller roster is never hopeless.
              </EmptyState>
              <div className="tiny muted" style={{ marginTop: 'var(--gap-sm)' }}>
                Roster level ~{queue.rosterLevel} · {queue.eligible} members could field a team.
              </div>
              {canManage && (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={busy === 'queue'}
                  onClick={() => void toggleQueue(false)}
                  style={{ marginTop: 'var(--gap-sm)' }}
                >
                  Leave the queue
                </Button>
              )}
            </>
          ) : (
            <>
              <EmptyState title="No war right now">
                {canManage
                  ? 'Enter the queue and you will be matched with a Guild of similar standing.'
                  : 'Your Guild is not in a war. A leader or officer enters the next one.'}
              </EmptyState>
              {canManage && (
                <Button
                  variant="primary"
                  block
                  loading={busy === 'queue'}
                  onClick={() => void toggleQueue(true)}
                  style={{ marginTop: 'var(--gap-sm)' }}
                >
                  Enter the next war
                </Button>
              )}
            </>
          )}
        </Panel>
      ) : (
        <>
          <Panel title="⚔ Current War" hint={war.status}>
            <div className="row row--between" style={{ alignItems: 'center' }}>
              <div className="center" style={{ flex: 1 }}>
                <div className="tiny muted">{war.guildA.name}</div>
                <div className="mono" style={{ fontSize: 22 }}>{formatNumber(war.scoreA)}</div>
              </div>
              <div className="tiny muted">vs</div>
              <div className="center" style={{ flex: 1 }}>
                <div className="tiny muted">{war.guildB.name}</div>
                <div className="mono" style={{ fontSize: 22 }}>{formatNumber(war.scoreB)}</div>
              </div>
            </div>
            <Bar
              ratio={war.scoreA + war.scoreB > 0 ? war.scoreA / (war.scoreA + war.scoreB) : 0.5}
              variant="exp"
            />
            <div className="row row--between tiny muted" style={{ marginTop: 4 }}>
              <span>Ends in {formatDuration(war.secondsRemaining)}</span>
              <span>
                {war.attacksLeft} / {war.attacksPerDay} attacks left today
              </span>
            </div>
          </Panel>

          {!war.enlisted ? (
            war.status === 'PENDING' ? (
              <TeamBuilder warId={war.id} onEnlisted={() => state.reload()} />
            ) : (
              <Panel title="◆ Not enlisted">
                <EmptyState title="You are sitting this one out">
                  The war has already started, so teams are locked. Enlist before the next one
                  begins.
                </EmptyState>
              </Panel>
            )
          ) : (
            <Panel title="⚔ Enemy roster" hint="Pick a target">
              <p className="tiny muted">
                You learn more about a defender by fighting them. Hitting the same person again is
                worth less each time.
              </p>
              <div className="stack-sm" style={{ marginTop: 'var(--gap-sm)' }}>
                {war.enemies.map((enemy) => (
                  <div key={enemy.participantId} className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
                    <div className="row row--between">
                      <strong className="tiny">
                        {enemy.isPriorityTarget && <span title="Priority target">🎯 </span>}
                        {enemy.username}
                      </strong>
                      <span className="tiny muted">
                        Lv ~{enemy.avgLevel} · power {formatNumber(enemy.power)}
                      </span>
                    </div>

                    {/* Scouting: only what has been earned by fighting them. */}
                    <div className="row row--wrap tiny" style={{ gap: 3, marginTop: 4 }}>
                      {enemy.scout.members.map((member, index) => (
                        <span key={index} className="chip chip--sm">
                          {member.name} Lv{member.level}
                          {member.speed ? ` · spd ${member.speed}` : ''}
                        </span>
                      ))}
                      {enemy.scout.hidden > 0 && (
                        <span className="chip chip--sm">+{enemy.scout.hidden} unknown</span>
                      )}
                    </div>

                    {enemy.scoutingNote && (
                      <div className="tiny" style={{ fontStyle: 'italic', marginTop: 4 }}>
                        “{enemy.scoutingNote}”
                      </div>
                    )}

                    <div className="row row--between" style={{ marginTop: 6 }}>
                      <span className="tiny muted">
                        {enemy.timesAttacked === 0
                          ? 'Not yet attacked — full points'
                          : enemy.timesAttacked >= 3
                            ? 'Attacked 3+ times — worth nothing now'
                            : `Attacked ${enemy.timesAttacked}x — reduced points`}
                        {enemy.defenceWins > 0 && ` · ${enemy.defenceWins} successful defences`}
                      </span>
                      <Button
                        size="sm"
                        variant="primary"
                        loading={busy === enemy.participantId}
                        disabled={war.attacksLeft <= 0 || busy != null}
                        onClick={() => void doAttack(enemy.participantId)}
                      >
                        Battle
                      </Button>
                    </div>
                  </div>
                ))}
                {war.enemies.length === 0 && (
                  <p className="tiny muted">Nobody has enlisted on the other side yet.</p>
                )}
              </div>
            </Panel>
          )}

          <Panel title="◈ Your Guild" hint={`${war.allies.length} enlisted`}>
            <div className="stack-sm">
              {war.allies
                .slice()
                .sort((a, b) => b.points - a.points)
                .map((ally) => (
                  <div key={ally.userId} className="row row--between tiny">
                    <span>{ally.username}</span>
                    <span className="muted">
                      <span className="mono">{formatNumber(ally.points)}</span> pts · {ally.battlesWon}W
                      {ally.defenceWins > 0 && ` · ${ally.defenceWins} held`}
                    </span>
                  </div>
                ))}
            </div>
          </Panel>
        </>
      )}

      {replay && <BattleReplay battleId={replay} onClose={() => setReplay(null)} />}
    </div>
  );
}

/**
 * A Guild's war record and its rivalries — design PART 37 and 38.
 *
 * A rivalry only appears once two Guilds have actually met more than once,
 * because "we played them once" is a fixture, not a rivalry.
 */
function History() {
  const war = useAsync<WarResponse>(() => api.get('/wars'));
  const guildId = war.data?.guild?.id;
  const state = useAsync<WarHistoryResponse>(
    () => api.get(`/wars/history/${guildId}`),
    [guildId ?? ''],
  );

  if (!war.data?.guild) {
    return (
      <Panel title="▤ War history">
        <EmptyState title="You are not in a Guild">
          Join one and its record will build here.
        </EmptyState>
      </Panel>
    );
  }
  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <p className="tiny muted">Reading the records…</p>;

  const { record, seasons, recent, rivalries } = state.data;
  const rivals = rivalries.filter((r): r is NonNullable<typeof r> => r != null && r.wars > 1);

  return (
    <div className="stack">
      <Panel title="▤ Record" hint={state.data.guild.name}>
        {!record || record.wars === 0 ? (
          <EmptyState title="No wars fought yet">
            Your Guild's record starts with its first war.
          </EmptyState>
        ) : (
          <>
            <div
              className="grid"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 8 }}
            >
              <Stat label="Wars" value={formatNumber(record.wars)} />
              <Stat label="Won" value={formatNumber(record.wins)} />
              <Stat label="Lost" value={formatNumber(record.losses)} />
              <Stat
                label="Streak"
                value={record.streak > 0 ? `${record.streak}W` : record.streak < 0 ? `${-record.streak}L` : '—'}
              />
              <Stat label="Best tier" value={record.bestTier.name} />
              <Stat label="Points" value={formatNumber(record.totalPoints)} />
            </div>
          </>
        )}
      </Panel>

      {rivals.length > 0 && (
        <Panel title="⚔ Rivalries" hint="Guilds you keep meeting">
          <div className="stack-sm">
            {rivals.map((rival) => (
              <div key={rival.opponent.id} className="row row--between tiny">
                <span>{rival.opponent.name}</span>
                <span className="mono">
                  {rival.ourWins} – {rival.theirWins}
                  {rival.draws > 0 && ` (${rival.draws}D)`}
                </span>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {recent.length > 0 && (
        <Panel title="◈ Recent wars">
          <div className="stack-sm">
            {recent.map((entry) => (
              <div key={entry.id} className="row row--between tiny">
                <span>
                  <span
                    className="chip chip--sm"
                    style={{ marginRight: 6 }}
                    title={entry.outcome}
                  >
                    {entry.outcome === 'win' ? 'W' : entry.outcome === 'loss' ? 'L' : 'D'}
                  </span>
                  vs {entry.opponent.name}
                </span>
                <span className="muted">
                  <span className="mono">
                    {formatNumber(entry.scoreFor)}–{formatNumber(entry.scoreAgainst)}
                  </span>
                  {entry.ratingDelta != null && (
                    <span> · {entry.ratingDelta >= 0 ? '+' : ''}{entry.ratingDelta}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {seasons.length > 0 && (
        <Panel title="◔ Seasons">
          <div className="stack-sm">
            {seasons.map((season) => (
              <div key={season.seasonKey} className="row row--between tiny">
                <span>{season.seasonKey}</span>
                <span className="muted">
                  <span className="chip chip--sm" style={{ background: season.tier.accent, color: '#1a1420', marginRight: 6 }}>
                    {season.tier.name}
                  </span>
                  peak <span className="mono">{formatNumber(season.peakRating)}</span> · {season.wins}W {season.losses}L
                </span>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stack-sm" style={{ gap: 0 }}>
      <div className="tiny muted">{label}</div>
      <div className="mono">{value}</div>
    </div>
  );
}

function Ladder() {
  const state = useAsync<LadderResponse>(() => api.get('/wars/ladder'));

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <p className="tiny muted">Reading the standings…</p>;

  return (
    <Panel title="▤ Ladder" hint={`Season ${state.data.seasonKey}`}>
      {state.data.rows.length === 0 ? (
        <EmptyState title="Nobody has fought yet">
          The ladder fills as Guilds finish their first wars.
        </EmptyState>
      ) : (
        <div className="stack-sm">
          {state.data.rows.map((row) => (
            <div key={row.guild.id} className="row row--between tiny">
              <span>
                <span className="mono muted" style={{ marginRight: 6 }}>
                  #{row.rank}
                </span>
                <Link to={`/guilds/${row.guild.key}`}>{row.guild.name}</Link>
              </span>
              <span>
                <span
                  className="chip chip--sm"
                  style={{ background: row.tier.accent, color: '#1a1420', marginRight: 6 }}
                >
                  {row.tier.name}
                </span>
                <span className="mono">{formatNumber(row.rating)}</span>
                <span className="muted">
                  {' '}
                  {row.wins}W {row.losses}L
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
      <p className="tiny muted" style={{ marginTop: 'var(--gap-sm)' }}>
        Guilds are matched against others of comparable roster level, so a tier reflects how you do
        against your peers rather than raw Pokémon levels.
      </p>
    </Panel>
  );
}
